import { useState } from 'react';
import axios from 'axios';
import { Dice5, Play, Plus, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

const OUTCOME_COLOR = {
    natural: '#39FF14', point_hit: '#39FF14',
    craps: '#FF3B30', seven_out: '#FF3B30',
    point_set: '#FFD700', no_decision: '#A0AAB2',
};

const PRESETS = {
    'Natural 7 (come-out win)': {
        shooter_id: 'blake', main_wager: '100',
        coverages: [{ user_id: 'opp1', amount: '100', is_bot: false }],
        rolls: [{ die1: 3, die2: 4 }],
    },
    'Point 8 then Seven-out (3 opps)': {
        shooter_id: 'blake', main_wager: '100',
        coverages: [
            { user_id: 'p2', amount: '30', is_bot: false },
            { user_id: 'p3', amount: '20', is_bot: false },
            { user_id: 'bot1', amount: '50', is_bot: true },
        ],
        rolls: [{ die1: 3, die2: 5 }, { die1: 3, die2: 4 }],
    },
    'Craps 12 (come-out lose)': {
        shooter_id: 'blake', main_wager: '50',
        coverages: [{ user_id: 'opp', amount: '50', is_bot: false }],
        rolls: [{ die1: 6, die2: 6 }],
    },
    'Partial cover 60/100, YO 11': {
        shooter_id: 'blake', main_wager: '100',
        coverages: [{ user_id: 'opp', amount: '60', is_bot: false }],
        rolls: [{ die1: 5, die2: 6 }],
    },
};

/**
 * StreetDiceSimPanel — admin dev-tool that runs the Street Dice engine
 * against a hand-crafted scenario and shows the ledger ops that would fire.
 * No wallet writes, no customer exposure. Purely for QA before Session 2
 * wires up real customer flow.
 */
export default function StreetDiceSimPanel() {
    const [scenario, setScenario] = useState(PRESETS['Natural 7 (come-out win)']);
    const [result, setResult] = useState(null);
    const [running, setRunning] = useState(false);

    const loadPreset = (name) => { setScenario(PRESETS[name]); setResult(null); };

    const addCover = () => setScenario((s) => ({
        ...s, coverages: [...s.coverages, { user_id: `p${s.coverages.length + 2}`, amount: '10', is_bot: false }],
    }));
    const removeCover = (i) => setScenario((s) => ({
        ...s, coverages: s.coverages.filter((_, idx) => idx !== i),
    }));
    const patchCover = (i, patch) => setScenario((s) => ({
        ...s, coverages: s.coverages.map((c, idx) => idx === i ? { ...c, ...patch } : c),
    }));

    const addRoll = () => setScenario((s) => ({
        ...s, rolls: [...s.rolls, { die1: 3, die2: 4 }],
    }));
    const removeRoll = (i) => setScenario((s) => ({
        ...s, rolls: s.rolls.filter((_, idx) => idx !== i),
    }));
    const patchRoll = (i, patch) => setScenario((s) => ({
        ...s, rolls: s.rolls.map((r, idx) => idx === i ? { ...r, ...patch } : r),
    }));

    const run = async () => {
        setRunning(true);
        try {
            const { data } = await axios.post(`${API}/admin/street-dice/simulate`,
                scenario, { withCredentials: true });
            setResult(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Simulation failed');
        } finally { setRunning(false); }
    };

    return (
        <div className="rounded-2xl border border-[#B266FF]/40 bg-[#100a18] p-4 space-y-3"
            data-testid="sd-sim-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Dice5 className="w-5 h-5 text-[#B266FF]" />
                    <h3 className="text-sm uppercase font-black tracking-wide text-white">
                        Street Dice — Engine Simulator
                    </h3>
                    <span className="text-[10px] text-[#A0AAB2]">admin QA · no wallet writes</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {Object.keys(PRESETS).map((n) => (
                        <button key={n} onClick={() => loadPreset(n)}
                            className="text-[11px] px-2 py-0.5 rounded-full border border-white/10 text-[#A0AAB2] hover:text-white hover:border-white/30"
                            data-testid={`sd-sim-preset-${n.split(' ')[0].toLowerCase()}`}>
                            {n}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* ── Scenario builder ─────────────────────────── */}
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                        <Field label="Shooter ID" value={scenario.shooter_id}
                            onChange={(v) => setScenario((s) => ({ ...s, shooter_id: v }))}
                            testId="sd-sim-shooter" />
                        <Field label="Main wager" prefix="$" value={scenario.main_wager}
                            onChange={(v) => setScenario((s) => ({ ...s, main_wager: v }))}
                            testId="sd-sim-wager" />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold">Coverages</p>
                            <button onClick={addCover} className="text-[11px] text-[#39FF14] hover:underline flex items-center gap-1">
                                <Plus className="w-3 h-3" />Add
                            </button>
                        </div>
                        <div className="space-y-1">
                            {scenario.coverages.map((c, i) => (
                                <div key={i} className="grid grid-cols-[1fr,90px,60px,24px] gap-1 items-center">
                                    <Input value={c.user_id} onChange={(e) => patchCover(i, { user_id: e.target.value })}
                                        className="bg-black/40 border-white/10 text-white h-8 text-xs" />
                                    <Input value={c.amount} onChange={(e) => patchCover(i, { amount: e.target.value })}
                                        className="bg-black/40 border-white/10 text-white h-8 text-xs font-mono" />
                                    <label className="text-[10px] text-[#A0AAB2] flex items-center gap-1">
                                        <input type="checkbox" checked={c.is_bot}
                                            onChange={(e) => patchCover(i, { is_bot: e.target.checked })} />
                                        bot
                                    </label>
                                    <button onClick={() => removeCover(i)} className="text-[#FF3B30] hover:opacity-70">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold">Forced rolls</p>
                            <button onClick={addRoll} className="text-[11px] text-[#39FF14] hover:underline flex items-center gap-1">
                                <Plus className="w-3 h-3" />Add
                            </button>
                        </div>
                        <div className="space-y-1">
                            {scenario.rolls.map((r, i) => (
                                <div key={i} className="grid grid-cols-[60px,60px,1fr,24px] gap-1 items-center">
                                    <Input type="number" min={1} max={6} value={r.die1}
                                        onChange={(e) => patchRoll(i, { die1: parseInt(e.target.value, 10) || 1 })}
                                        className="bg-black/40 border-white/10 text-white h-8 text-xs font-mono text-center" />
                                    <Input type="number" min={1} max={6} value={r.die2}
                                        onChange={(e) => patchRoll(i, { die2: parseInt(e.target.value, 10) || 1 })}
                                        className="bg-black/40 border-white/10 text-white h-8 text-xs font-mono text-center" />
                                    <span className="text-[11px] text-[#FFD700] font-mono">= {r.die1 + r.die2}</span>
                                    <button onClick={() => removeRoll(i)} className="text-[#FF3B30] hover:opacity-70">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    <Button onClick={run} disabled={running}
                        className="w-full bg-[#B266FF] hover:bg-[#B266FF]/90 text-white font-black"
                        data-testid="sd-sim-run">
                        {running ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                        Run simulation
                    </Button>
                </div>

                {/* ── Results ──────────────────────────────────── */}
                <div className="rounded-lg bg-black/40 border border-white/10 p-3 min-h-[280px]" data-testid="sd-sim-results">
                    {!result ? (
                        <p className="text-[#A0AAB2] italic text-sm">Run a scenario to see the engine&apos;s ledger ops.</p>
                    ) : (
                        <>
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                <span className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold">Result</span>
                                <span className="font-black uppercase text-xs px-2 py-0.5 rounded-full"
                                    style={{ backgroundColor: `${OUTCOME_COLOR[result.settlement_outcome] || '#666'}25`,
                                             color: OUTCOME_COLOR[result.settlement_outcome] || '#fff' }}>
                                    {result.settlement_outcome || 'unsettled'}
                                </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-[11px] mb-3">
                                <Stat label="Total covered" value={fmt(result.total_covered)} />
                                <Stat label="Matched" value={fmt(result.matched)} color="text-[#39FF14]" />
                                <Stat label="Refunded" value={fmt(result.unmatched)} color="text-[#FFD700]" />
                            </div>
                            <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-1">Rolls played</p>
                            <div className="flex gap-1 flex-wrap mb-3">
                                {result.rolls.map((r, i) => (
                                    <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-white/10 text-white">
                                        {r.die1}+{r.die2}={r.total}
                                        <span className="ml-1" style={{ color: OUTCOME_COLOR[r.outcome] }}>·{r.outcome.replace('_', ' ')}</span>
                                    </span>
                                ))}
                            </div>
                            <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-1">Net per user</p>
                            <ul className="text-[11px] mb-3 divide-y divide-white/5">
                                {Object.entries(result.net_by_user).map(([u, v]) => (
                                    <li key={u} className="flex justify-between py-1">
                                        <span className="text-white">{u}</span>
                                        <span className={`font-mono font-black ${Number(v) > 0 ? 'text-[#39FF14]' : Number(v) < 0 ? 'text-[#FF3B30]' : 'text-[#FFD700]'}`}>
                                            {Number(v) >= 0 ? '+' : ''}{fmt(v)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-1">Ledger operations</p>
                            <ul className="text-[10px] font-mono space-y-0.5">
                                {result.ledger_ops.map((op, i) => (
                                    <li key={i} className="text-[#A0AAB2]">
                                        <span className="text-white">{op.user_id}</span>
                                        <span className="mx-1">·</span>
                                        <span style={{ color: op.kind.includes('payout') ? '#39FF14' : op.kind.includes('loss') ? '#FF3B30' : '#FFD700' }}>
                                            {op.kind}
                                        </span>
                                        <span className="mx-1">·</span>
                                        <span className="text-white">${op.amount}</span>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

const Field = ({ label, value, onChange, prefix, testId }) => (
    <div>
        <label className="block text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-0.5">{label}</label>
        <div className="flex items-center gap-1">
            {prefix && <span className="text-[#FFD700] font-mono">{prefix}</span>}
            <Input value={value} onChange={(e) => onChange(e.target.value)}
                className="bg-black/40 border-white/10 text-white h-8 text-xs font-mono"
                data-testid={testId} />
        </div>
    </div>
);

const Stat = ({ label, value, color }) => (
    <div className="rounded border border-white/10 bg-black/30 p-2">
        <p className="text-[9px] uppercase tracking-widest text-[#A0AAB2] font-bold">{label}</p>
        <p className={`font-mono font-black text-sm ${color || 'text-white'}`}>{value}</p>
    </div>
);
